import axios from 'axios';
import config from '../../config';

/**
 * Où regarder la créa d'un concurrent.
 *
 * ## La seule page qui montre la publicité seule
 *
 * La bibliothèque publicitaire ne communique pas les médias : ni l'API (`ad_creative_link_images`
 * revient vide), ni une intégration en iframe. La seule adresse qui affiche **la créa et rien
 * d'autre** est son rendu, `facebook.com/ads/archive/render_ad/?id=…&access_token=…`.
 *
 * Le permalien public `facebook.com/ads/library/?id=…` existe et ne demande pas de jeton, mais
 * il ouvre la **page entière de l'annonceur** avec ses autres publicités, sa recherche et ses
 * filtres, la créa étant posée dans une petite fenêtre par-dessus. Il sert donc de repli, pas
 * de chemin principal.
 *
 * ## Pourquoi demander l'adresse au serveur
 *
 * L'`adSnapshotUrl` enregistré porte le jeton **du jour de la synchronisation**. Il périme, et
 * la page répond alors « Ce contenu n'est pas disponible » — ce qui se lit comme une publicité
 * retirée par l'annonceur. Le serveur, lui, connaît le jeton valide aujourd'hui et reconstruit
 * l'adresse à la demande.
 *
 * ⚠️ Le jeton se retrouve dans l'URL de l'onglet ouvert. Meta l'exige en paramètre et aucune
 * autre adresse ne rend la créa isolée : c'est le prix de ce qu'on affiche.
 */

/**
 * Ouvre la publicité dans un onglet.
 *
 * ## Deux pièges, et ils se cumulent
 *
 * 1. **L'onglet s'ouvre avant l'appel réseau.** Un `window.open` lancé après un `await` n'est
 *    plus rattaché au clic par le navigateur, qui le bloque comme une popup. On ouvre donc un
 *    onglet vide tout de suite, et on lui pose sa destination au retour.
 * 2. **⚠️ `noopener` fait rendre `null` à `window.open`.** C'est écrit dans la spécification :
 *    sans lien vers la fenêtre ouvrante, il n'y a pas de poignée à rendre. L'onglet s'ouvrait
 *    donc bien — vide — mais le code le croyait bloqué, retombait sur un second `window.open`
 *    après l'`await`, et celui-là était refusé pour de bon. Résultat vu à l'usage : un onglet
 *    blanc, et rien d'autre.
 *
 * La référence inverse est coupée à la main (`onglet.opener = null`) juste avant de naviguer :
 * on garde la protection que `noopener` apportait, sans perdre la poignée.
 *
 * @param {{id: string}} publicite
 * @param {string} token  le jeton de session Kokpit
 * @returns {Promise<{ouverte: boolean, rendu?: boolean, raison?: string}>}
 */
export async function ouvrirVisuel(publicite, token) {
  if (!publicite?.id) return { ouverte: false, raison: 'publicité sans identifiant' };

  const onglet = window.open('', '_blank');
  if (!onglet) return { ouverte: false, raison: 'onglet bloqué par le navigateur' };

  try {
    const reponse = await axios.get(
      `${config.API_URL}/api/competitors/ads/${publicite.id}/visuel`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const { url, rendu, raison } = reponse.data || {};

    if (!url) {
      onglet.close();
      return { ouverte: false, raison: raison || 'aucune adresse disponible' };
    }

    // La référence inverse est coupée avant la navigation : la page ouverte ne pourra pas
    // manipuler celle d'où elle vient.
    try { onglet.opener = null; } catch (sansImportance) { /* certains navigateurs l'interdisent */ }
    onglet.location.replace(url);

    return { ouverte: true, rendu, raison };
  } catch (erreur) {
    onglet.close();
    return { ouverte: false, raison: erreur?.response?.data?.error || erreur.message };
  }
}

export default ouvrirVisuel;
